<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import PlotlyWidget from "@/components/datascience/PlotlyWidget.vue";
import CopyableElement from "@/components/ui/CopyableElement.vue";
import ExpandableCard from "@/components/ui/ExpandableCard.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import {
  getReportName,
  isCrossValidationReport,
  MediaType,
  type CrossValidationReportDto,
  type EstimatorReportDto,
} from "@/dto";
import { useClipboard } from "@/services/clipboard";
import {
  createInspectionPlotDecoration,
  isCoefficientsBoxFormat,
  isMdiBoxFormat,
  makeBarplot,
  makeBoxPlot,
  normalizeCoefficientsDataframe,
  normalizeMdiDataframe,
  normalizePermutationImportanceDataframe,
  type InspectionDataframe,
} from "@/services/inspectionHelpers";
import type { PlotlyWidgetSpec } from "@/components/datascience/PlotlyWidget.vue";
import { useDatasourceStore } from "@/stores/datasource";
import { initPipelineBehavior, stripScriptTags } from "@/views/reports/estimatorPipeline";

type InspectionPlotSpec = PlotlyWidgetSpec | { _mdiUnavailable: true };

const {
  report,
  tracePrefix = "",
  inspectionDataframes: inspectionDataframesProp,
} = defineProps<{
  report: EstimatorReportDto | CrossValidationReportDto;
  tracePrefix?: string;
  inspectionDataframes?: InspectionDataframe[];
}>();

const clipboard = useClipboard();
const datasource = useDatasourceStore();

const html = ref("");
const inspectionDataframes = ref<InspectionDataframe[]>([]);
const effectiveInspectionDataframes = computed(
  () => inspectionDataframesProp ?? inspectionDataframes.value
);
const hasPermutationImportance = computed(() =>
  effectiveInspectionDataframes.value.some((df) => df.method === "permutation_importance")
);

const expandableCard = useTemplateRef<InstanceType<typeof ExpandableCard>>("expandableCard");
const pipelineContentRef = useTemplateRef<HTMLElement>("pipelineContentRef");

const PERMUTATION_SNIPPET_FULL = `# report refers to your skore-lib Report instance
report.inspection.permutation_importance(data_source="train")
report.inspection.permutation_importance(data_source="test")

# project refers to a skore Project instance
project.put("my report name", report)`;

const PERMUTATION_SNIPPET_TRAIN_ONLY = `# report refers to your skore-lib Report instance
report.inspection.permutation_importance(data_source="train")

# project refers to a skore Project instance
project.put("my report name", report)`;

const PERMUTATION_SNIPPET_TEST_ONLY = `# report refers to your skore-lib Report instance
report.inspection.permutation_importance(data_source="test")

# project refers to a skore Project instance
project.put("my report name", report)`;

const PERMUTATION_MESSAGE =
  "Permutation importance has not been computed for the selected set. Run the following to add it:";

const permutationDatasourcesAvailable = computed(() => {
  const dfs = effectiveInspectionDataframes.value.filter(
    (df) => df.method === "permutation_importance" && df.datasource != null
  );
  return new Set(dfs.map((df) => df.datasource as string));
});

const missingPermutationDatasources = computed(() => {
  const available = permutationDatasourcesAvailable.value;
  const missing: string[] = [];
  if (!available.has("train")) missing.push("train");
  if (!available.has("test")) missing.push("test");
  return missing;
});

const permutationImportanceSnippet = computed(() => {
  const missing = missingPermutationDatasources.value;
  if (missing.length === 0) return PERMUTATION_SNIPPET_FULL;
  if (missing.length === 2) return PERMUTATION_SNIPPET_FULL;
  return missing[0] === "train" ? PERMUTATION_SNIPPET_TRAIN_ONLY : PERMUTATION_SNIPPET_TEST_ONLY;
});

const permutationImportanceSnippetMarkdown = computed(
  () => "```python\n" + permutationImportanceSnippet.value + "\n```"
);

const permutationImportanceMessage = computed(() => {
  const missing = missingPermutationDatasources.value;
  if (missing.length === 0) return null;
  if (!hasPermutationImportance.value) return null;
  return PERMUTATION_MESSAGE;
});

const showMissingPermutationSnippet = computed(
  () =>
    hasPermutationImportance.value &&
    datasource.sourceName != null &&
    missingPermutationDatasources.value.includes(datasource.sourceName)
);

const reportMediaHtml = computed(() =>
  report.medias.find((r) => r.category === "model" && r.content_type === "text/html")
);

const inspectionPlotSpecs = computed(() => {
  const filtered = effectiveInspectionDataframes.value.filter(
    (df) => df.datasource === datasource.sourceName || df.datasource === null
  );
  const reportLabel = getReportName(report);
  const datasetLabel = datasource.sourceName ?? "train";
  const map = new Map<string, InspectionPlotSpec>(
    filtered.map((df) => {
      const decoration = createInspectionPlotDecoration(df.method, {
        reportLabel,
        datasetLabel,
        mlTask: report.ml_task,
        tracePrefix,
      });

      switch (df.method) {
        case "permutation_importance": {
          const dfForPlot = normalizePermutationImportanceDataframe(
            df,
            report.ml_task ?? "unknown",
            datasource.sourceName ?? ""
          );
          return [df.method, makeBoxPlot({ ...decoration, dataframes: [dfForPlot] })];
        }
        case "impurity_decrease": {
          if (datasource.sourceName === "test") {
            return [df.method, { _mdiUnavailable: true as const }];
          }
          const dfForPlot = normalizeMdiDataframe(df);
          const useBox = isMdiBoxFormat(dfForPlot);
          return [
            df.method,
            useBox
              ? makeBoxPlot({ ...decoration, dataframes: [dfForPlot] })
              : makeBarplot({ ...decoration, dataframes: [dfForPlot] }),
          ];
        }
        case "coefficients": {
          const dfForPlot = normalizeCoefficientsDataframe(df);
          const isCoeffMulti =
            dfForPlot && typeof dfForPlot === "object" && "dataframes" in dfForPlot;
          const dataframes: InspectionDataframe[] = isCoeffMulti
            ? (dfForPlot as { dataframes: InspectionDataframe[] }).dataframes
            : [dfForPlot as InspectionDataframe];
          const legendTitle = isCoeffMulti
            ? (dfForPlot as { legendTitle: "Label" | "Output" }).legendTitle
            : undefined;
          const useBox = isCoefficientsBoxFormat(dataframes);
          return [
            df.method,
            useBox
              ? makeBoxPlot({ ...decoration, dataframes, legendTitle })
              : makeBarplot({ ...decoration, dataframes, legendTitle }),
          ];
        }
        default: {
          return [df.method, makeBarplot({ ...decoration, dataframes: [df] })];
        }
      }
    })
  );
  if (datasource.sourceName === "test") {
    const mdiDf = effectiveInspectionDataframes.value.find(
      (df) => df.method === "impurity_decrease"
    );
    if (mdiDf && mdiDf.method && !map.has(mdiDf.method)) {
      map.set(mdiDf.method, { _mdiUnavailable: true as const });
    }
  }
  return map;
});

async function fetchHtml() {
  if (reportMediaHtml.value) {
    const response = await fetch(reportMediaHtml.value.presigned_url);
    const rawHtml = await response.text();
    html.value = stripScriptTags(rawHtml);
    await nextTick();
    initPipelineBehavior(pipelineContentRef.value);
  } else {
    html.value = "";
  }
}

async function fetchInspectionDataframes() {
  if (isCrossValidationReport(report)) {
    return;
  }
  if (!report || !Array.isArray(report.medias)) {
    return;
  }

  const promises = report.medias
    .filter((m) => m.category === "inspection" && m.content_type === MediaType.DataFrame)
    .map(async (m) => {
      const response = await fetch(m.presigned_url);
      const representation = await response.json();
      return {
        method: m.method,
        verboseMethodName: m.verbose_name.replace("Inspection - ", ""),
        indexNames: representation.index_names,
        datasource: m.data_source,
        ...representation,
      } as InspectionDataframe;
    });

  inspectionDataframes.value = await Promise.all(promises);
}

async function copyPermutationImportanceSnippet() {
  await clipboard.copyText(permutationImportanceSnippet.value);
}

function expand(): Promise<void> {
  return expandableCard.value?.expand() ?? Promise.resolve();
}

onMounted(async () => {
  await fetchHtml();
  await fetchInspectionDataframes();
});

watch(
  () => report,
  async () => {
    html.value = "";
    inspectionDataframes.value = [];
    await fetchHtml();
    await fetchInspectionDataframes();
  }
);

defineExpose({ expand });
</script>

<template>
  <ExpandableCard ref="expandableCard" title="Model pipeline" icon="icon-divider">
    <template #actions></template>

    <div class="pipeline">
      <CopyableElement>
        <div ref="pipelineContentRef" v-html="html" />
      </CopyableElement>
    </div>

    <template #expandableContent>
      <div id="inspection" class="card-content inspection">
        <template v-for="[name, plotSpec] in inspectionPlotSpecs.entries()" :key="name">
          <CopyableElement v-if="!('_mdiUnavailable' in plotSpec)" :id="`inspection-${name}`">
            <PlotlyWidget :spec="plotSpec" />
          </CopyableElement>
          <div v-else :id="`inspection-${name}`" class="inspection-unavailable-message">
            <header class="text-base-regular">
              <i class="icon icon-info-circle-2" />
              Mean decrease in impurity is available on the train set. Switch dataset on the left
              menu.
            </header>
          </div>
        </template>
        <div
          v-if="showMissingPermutationSnippet"
          id="inspection-permutation_importance"
          class="inspection-message-container text-base-regular"
        >
          <header class="text-base-regular">
            <i class="icon icon-info-circle-2" />
            {{ permutationImportanceMessage }}
          </header>

          <div class="inspection-message-container-body">
            <div class="code-snippet">
              <div class="actions">
                <FloatingTooltip text="Copy to clipboard">
                  <SimpleButton
                    icon-left="icon-copy"
                    kind="ghost"
                    @click="copyPermutationImportanceSnippet"
                  />
                </FloatingTooltip>
              </div>

              <MarkdownWidget :source="permutationImportanceSnippetMarkdown" />
            </div>
          </div>
        </div>
        <div
          v-if="!hasPermutationImportance"
          class="inspection-message-container text-base-regular"
        >
          <header class="text-base-regular">
            <i class="icon icon-info-circle-2" />
            Permutation importance has not been computed yet for this report. Run the following code
            to compute it now:
          </header>

          <div class="inspection-message-container-body">
            <div class="code-snippet">
              <div class="actions">
                <FloatingTooltip text="Copy to clipboard">
                  <SimpleButton
                    icon-left="icon-copy"
                    kind="ghost"
                    @click="copyPermutationImportanceSnippet"
                  />
                </FloatingTooltip>
              </div>

              <MarkdownWidget :source="permutationImportanceSnippetMarkdown" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </ExpandableCard>
</template>

<style scoped>
:deep(.card-content:has(.pipeline)) {
  overflow: hidden;
  padding: 0;
}

.pipeline {
  background-image: radial-gradient(var(--content-disabled) 1px, transparent 0);
  background-position: -6px -7px;
  background-size: 13px 15px;
  overflow-x: auto;

  .copyable-element {
    display: flex;
    min-width: min-content;
    justify-content: center;
    padding: var(--numbers-18);
    border: 0;
  }
}

.inspection {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-8) var(--numbers-16);

  .copyable-element {
    overflow: hidden;
    width: 100%;
    min-width: 0;
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-6);
  }

  header {
    display: flex;
    align-items: center;
    gap: var(--numbers-8);
  }

  .inspection-unavailable-message,
  .inspection-message-container {
    padding: var(--numbers-12);
    border: solid var(--stroke-width-md) var(--border-tertiary);
    border-radius: var(--numbers-6);
  }

  .inspection-unavailable-message header,
  .inspection-message-container header {
    display: flex;
    align-items: center;
    gap: var(--numbers-8);
  }

  .inspection-message-container {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-12);

    .inspection-message-container-body .code-snippet {
      position: relative;
      padding: var(--numbers-12);
      border: var(--stroke-width-md) solid var(--border-tertiary);
      border-radius: var(--numbers-6);
      background: var(--background-secondary);

      .actions {
        position: absolute;
        top: var(--numbers-8);
        right: var(--numbers-8);
      }

      :deep(.hljs),
      :deep(.hljs-comment),
      :deep(.hljs-quote) {
        background: var(--background-secondary);
      }
    }
  }

  .inspection-plot {
    padding: var(--numbers-12);
  }
}

:deep(.inspection .copyable-element .element) {
  width: 100%;
  min-width: 0;
}
</style>
